import { useMutation, useQueryClient } from '@tanstack/react-query'
import { invalidateQueriesSafe } from '@/api/invalidate-queries-safe'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

interface PauseInstrumentInput {
  campaignId: string
  loiId: string
  instrumentId: string
}

interface StopInstrumentInput extends PauseInstrumentInput {
  stopReason: string
  stopReasonOther?: string | null
}

export function useStopInstrument() {
  const qc = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async ({ instrumentId, stopReason, stopReasonOther }: StopInstrumentInput) => {
      await apiClient.post(`/api/v1/instruments/${instrumentId}/stop`, {
        stopReason,
        stopReasonOther,
      })
    },
    onSuccess: (_data, { campaignId, loiId, instrumentId }) => {
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.instrumentDashboard.summary(tenantKey(tenantId), instrumentId),
      })
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.loiDashboard.all(tenantKey(tenantId)),
      })
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.instruments.list(tenantKey(tenantId)),
      })
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.instruments.all(tenantKey(tenantId)),
      })
      if (campaignId) {
        invalidateQueriesSafe(qc, {
          queryKey: queryKeys.campaigns.instruments(tenantKey(tenantId), campaignId),
        })
      }
      if (loiId) {
        invalidateQueriesSafe(qc, {
          queryKey: queryKeys.loiDashboard.instruments(tenantKey(tenantId), loiId, 'all'),
        })
      }
    },
  })
}

export function useDeleteDraftInstrument() {
  const qc = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async ({ instrumentId }: PauseInstrumentInput) => {
      await apiClient.delete(`/api/v1/instruments/${instrumentId}`)
    },
    onSuccess: (_data, { campaignId, loiId }) => {
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.instruments.list(tenantKey(tenantId)),
      })
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.instruments.all(tenantKey(tenantId)),
      })
      if (campaignId) {
        invalidateQueriesSafe(qc, {
          queryKey: queryKeys.campaigns.instruments(tenantKey(tenantId), campaignId),
        })
      }
      if (loiId) {
        invalidateQueriesSafe(qc, {
          queryKey: queryKeys.loiDashboard.instruments(tenantKey(tenantId), loiId, 'all'),
        })
      }
    },
  })
}

export function usePauseInstrument() {
  const qc = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)

  return useMutation({
    mutationFn: async ({ instrumentId }: PauseInstrumentInput) => {
      await apiClient.post(`/api/v1/instruments/${instrumentId}/pause`)
    },
    onSuccess: (_data, { campaignId, loiId, instrumentId }) => {
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.instrumentDashboard.summary(
          tenantKey(tenantId),
          instrumentId,
        ),
      })
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.loiDashboard.all(tenantKey(tenantId)),
      })
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.instruments.list(tenantKey(tenantId)),
      })
      invalidateQueriesSafe(qc, {
        queryKey: queryKeys.instruments.all(tenantKey(tenantId)),
      })
      if (campaignId) {
        invalidateQueriesSafe(qc, {
          queryKey: queryKeys.campaigns.instruments(tenantKey(tenantId), campaignId),
        })
      }
      if (loiId) {
        invalidateQueriesSafe(qc, {
          queryKey: queryKeys.loiDashboard.instruments(
            tenantKey(tenantId),
            loiId,
            'all',
          ),
        })
      }
    },
  })
}
